import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type MenuCartModificadorLine = {
    id: number
    categoriaId: number
    descripcion: string
    precioAdicional: number
}

export type MenuCartComboItem = {
    elementoId: number
    elementoDescripcion: string
    peTipo?: string | null
    platilloId: number
    descripcion: string
    pathImg: string
    /** Costo adicional COMBO (upgrade). */
    precioAdicional: number
    /** Precio de lista del platillo (MXN / referencia). */
    precioLista: number
    /** % descuento MXN sobre precio de lista (0 en participantes). */
    porcentajeDescuento: number
    modificadores: MenuCartModificadorLine[]
}

export type MenuCartTipo = 'platillo' | 'combo' | 'mxn' | 'seleccion_n'

export type MenuCartLine = {
    key: string
    tipo: MenuCartTipo
    platilloId: number
    descripcion: string
    pathImg: string
    cantidad: number
    precioUnitarioBase: number
    precioUnitario: number
    happyHourAplicado: boolean
    happyHourPorcentaje: number | null
    modificadores: MenuCartModificadorLine[]
    comboItems: MenuCartComboItem[]
    categoriaId: number | null
}

type CartBySlug = Record<string, MenuCartLine[]>

type AddLinePayload = Omit<MenuCartLine, 'key' | 'cantidad' | 'tipo' | 'comboItems' | 'modificadores'> & {
    cantidad?: number
    tipo?: MenuCartTipo
    modificadores?: MenuCartModificadorLine[]
    comboItems?: MenuCartComboItem[]
}

type MenuCartState = {
    carts: CartBySlug
    addLine: (slug: string, line: AddLinePayload) => void
    updateCantidad: (slug: string, lineKey: string, cantidad: number) => void
    removeLine: (slug: string, lineKey: string) => void
    clearCart: (slug: string) => void
    getLines: (slug: string) => MenuCartLine[]
    getItemCount: (slug: string) => number
    getSubtotal: (slug: string) => number
}

const modsFingerprint = (modificadores: MenuCartModificadorLine[]): string =>
    [...modificadores]
        .map((mod) => `${mod.categoriaId}:${mod.id}`)
        .sort()
        .join(',')

const comboFingerprint = (items: MenuCartComboItem[]): string =>
    items
        .map(
            (item) =>
                `${item.elementoId}:${item.platilloId}:${item.precioAdicional}:${item.precioLista}:${item.porcentajeDescuento}:${modsFingerprint(item.modificadores)}`
        )
        .join(';')

const buildLineKey = (line: {
    tipo: MenuCartTipo
    platilloId: number
    precioUnitario: number
    happyHourAplicado: boolean
    modificadores: MenuCartModificadorLine[]
    comboItems: MenuCartComboItem[]
}): string => {
    if (line.tipo === 'combo' || line.tipo === 'mxn' || line.tipo === 'seleccion_n') {
        return `${line.tipo}|${line.platilloId}|${line.precioUnitario}|${comboFingerprint(line.comboItems)}`
    }

    return `platillo|${line.platilloId}|${line.precioUnitario}|${line.happyHourAplicado ? 1 : 0}|${modsFingerprint(line.modificadores)}`
}

const itemModsTotal = (item: MenuCartComboItem): number =>
    item.modificadores.reduce((sum, mod) => sum + mod.precioAdicional, 0)

/** Monto pagado del platillo MXN (lista con %); mods aparte. */
export const getMxnItemDishPrice = (item: MenuCartComboItem): number => {
    const lista = item.precioLista > 0 ? item.precioLista : 0
    const pct = item.porcentajeDescuento > 0 ? item.porcentajeDescuento : 0
    return Math.max(0, lista * (1 - pct / 100))
}

const lineExtras = (line: MenuCartLine): number => {
    const modsExtra = line.modificadores.reduce((sum, mod) => sum + mod.precioAdicional, 0)

    if (line.tipo === 'mxn' || line.tipo === 'seleccion_n') {
        const mxnExtra = line.comboItems.reduce(
            (sum, item) => sum + getMxnItemDishPrice(item) + itemModsTotal(item),
            0
        )
        return modsExtra + mxnExtra
    }

    const comboExtra = line.comboItems.reduce(
        (sum, item) => sum + item.precioAdicional + itemModsTotal(item),
        0
    )

    return modsExtra + comboExtra
}

const lineTotal = (line: MenuCartLine): number =>
    (line.precioUnitario + lineExtras(line)) * line.cantidad

export const useMenuCartStore = create<MenuCartState>()(
    persist(
        (set, get) => ({
            carts: {},
            addLine: (slug, payload) => {
                const cantidad = Math.max(1, payload.cantidad ?? 1)
                const tipo: MenuCartTipo = payload.tipo ?? 'platillo'
                const modificadores = payload.modificadores ?? []
                const comboItems = (payload.comboItems ?? []).map((item) => ({
                    ...item,
                    precioLista: item.precioLista ?? 0,
                    porcentajeDescuento: item.porcentajeDescuento ?? 0,
                    peTipo: item.peTipo ?? null,
                }))
                const normalized = {
                    ...payload,
                    tipo,
                    modificadores,
                    comboItems,
                }
                const key = buildLineKey(normalized)

                set((state) => {
                    const current = state.carts[slug] ?? []
                    const existingIndex = current.findIndex((line) => line.key === key)

                    if (existingIndex >= 0) {
                        const next = current.map((line, index) =>
                            index === existingIndex
                                ? { ...line, cantidad: line.cantidad + cantidad }
                                : line
                        )
                        return { carts: { ...state.carts, [slug]: next } }
                    }

                    const nextLine: MenuCartLine = {
                        ...normalized,
                        key,
                        cantidad,
                    }

                    return {
                        carts: {
                            ...state.carts,
                            [slug]: [...current, nextLine],
                        },
                    }
                })
            },
            updateCantidad: (slug, lineKey, cantidad) => {
                set((state) => {
                    const current = state.carts[slug] ?? []
                    if (cantidad <= 0) {
                        return {
                            carts: {
                                ...state.carts,
                                [slug]: current.filter((line) => line.key !== lineKey),
                            },
                        }
                    }

                    return {
                        carts: {
                            ...state.carts,
                            [slug]: current.map((line) =>
                                line.key === lineKey ? { ...line, cantidad } : line
                            ),
                        },
                    }
                })
            },
            removeLine: (slug, lineKey) => {
                set((state) => ({
                    carts: {
                        ...state.carts,
                        [slug]: (state.carts[slug] ?? []).filter((line) => line.key !== lineKey),
                    },
                }))
            },
            clearCart: (slug) => {
                set((state) => {
                    const current = state.carts[slug] ?? []
                    if (current.length === 0) return state
                    return {
                        carts: {
                            ...state.carts,
                            [slug]: [],
                        },
                    }
                })
            },
            getLines: (slug) => get().carts[slug] ?? [],
            getItemCount: (slug) =>
                (get().carts[slug] ?? []).reduce((sum, line) => sum + line.cantidad, 0),
            getSubtotal: (slug) =>
                (get().carts[slug] ?? []).reduce((sum, line) => sum + lineTotal(line), 0),
        }),
        {
            name: 'ir-menu-cart',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ carts: state.carts }),
            version: 3,
            migrate: (persisted) => {
                const state = persisted as { carts?: CartBySlug }
                if (!state?.carts) return { carts: {} }

                const carts: CartBySlug = {}
                Object.entries(state.carts).forEach(([slug, lines]) => {
                    carts[slug] = (lines ?? []).map((line) => ({
                        ...line,
                        tipo: line.tipo ?? 'platillo',
                        comboItems: (line.comboItems ?? []).map((item) => ({
                            ...item,
                            precioLista: item.precioLista ?? 0,
                            porcentajeDescuento: item.porcentajeDescuento ?? 0,
                            peTipo: item.peTipo ?? null,
                        })),
                        modificadores: line.modificadores ?? [],
                    }))
                })

                return { carts }
            },
        }
    )
)

export const getMenuCartLineTotal = lineTotal
export const getMenuCartLineExtras = lineExtras
